<template>
  <div :class="['flex items-center', layout]">
    <div id="forchange-video" class="video" style="min-height: 200px">
      <div v-show="loading" class="loading">
        <Loading />
      </div>
      <div v-show="!loading" ref="player" class="player">
        <div ref="video"></div>

        <div v-if="error" class="state" style="padding: 20px 0">
          <img class="icon" src="./assets/not-found.svg" alt="" style="height: 100px" />

          <p class="description" style="margin-top: 10px; color: white; font-weight: bold">
            {{ error }}
          </p>
          <button
            class="text-white bg-purple-600 hover:bg-purple-500 rounded-md px-8 py-2 mt-2"
            style="font-size: 14px"
            @click="onRefreshVideo"
          >
            刷新
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Loading from '../../../resources-loading';
import { Content } from '@forchange/storytime';
import { namespace } from 'vuex-class';

import Player from 'xgplayer';
import './styles/.xgplayer/skin/index';
import { HdvVideo } from '@forchange/cleword-utils';
import getVideoPlayListGQL from '@/store/modules/video/graphql/index';
import { VideoRateKey, PlayItem, Result, VideoRate, ResultWithObjPlayList } from './video.d';
import { Environment } from '@/store/modules/common/types';
import hls, { ErrorData } from 'hls.js';
import * as Sentry from '@sentry/vue';
import { UserInfo } from '@forchange/cleword-postmate';
import { ApolloError } from '@forchange/cleman-client';

const RECONNECT_LIMIT = 2;

const useHlsValue = () => {
  return hls.isSupported();
};

const commonModal = namespace('common');
@Component({
  name: 'Video',
  components: {
    Loading
  }
})
export default class Video extends Vue {
  @commonModal.State('environment') environment!: Environment;
  @commonModal.State('isPublic') isPublic!: Boolean;

  @Prop({ type: Object, default: () => {} }) content!: Content & {
    align: string;
    poster: string;
    autoFullscreen?: boolean;
    defaultSpeed: number;
    url: string;
  };
  loading = true;
  paused = true;
  player: Player | undefined;

  @commonModal.State('courseName') courseName: string;
  @commonModal.State('version') version!: string;
  @commonModal.State('userInfo') userInfo!: UserInfo;

  error = '';
  last_play_time: number | undefined = undefined;

  retryCount = 0;
  isRetry = false;
  definition: VideoRateKey = 'AUTO';

  isInit = false;

  videoRawSrc = ''

  get layout() {
    const layout: Array<String> = [];
    switch (this.content.align) {
      case '居中':
      case 'center':
        layout.push('justify-center');
        break;
      case '居右':
      case 'right':
        layout.push('justify-end');
        break;
      default:
        layout.push('justify-start');
        break;
    }
    return layout.join(' ');
  }

  get SentryVideoBaseContext() {
    return {
      url: this.content.url,
      src: this.player?.src
    };
  }

  get SentryCourseContext() {
    return {
      name: this.courseName,
      id: this.environment?.kind === 'public-preview' ? this.environment.sharedKey : ''
    };
  }

  mounted() {
    this.initVideo();
    this.loading = false;
  }

  setSentryContext(SentryVideoContext: Record<string, any>) {
    Sentry.setContext('course', this.SentryCourseContext);
    Sentry.setContext('surfer', {
      version: this.version
    });
    Sentry.setContext('userInfo', this.userInfo);
    Sentry.setContext('video', SentryVideoContext);
  }

  initAutoFullscreen() {
    this.$nextTick(() => {
      if (
        window.storyteller.topContet?.$id === this.content.$id &&
        ['是', true].includes(this.content?.autoFullscreen || '')
      ) {
        this.player?.getFullscreen(this.player.root);
      }
    });
  }

  initVideo() {
    this.error = '';
    this.$store.commit('video/setEndState', false);
    if (this.content.url.includes('vod')) {
      this.initStreamVideo();
    } else {
      this.initNormalVideo();
    }
  }

  handleReload() {
    this.player?.reload();
  }

  onRefreshVideo() {
    window.location.reload();
  }

  initVideoEvent() {
    this.player?.once('complete', () => {
      this.initAutoFullscreen();
    });
    this.player?.on('ended', this.handleEnd);
    this.player?.on('error', this.handleVideoError);

    this.player?.on('definitionChange', this.handleDefinitionChange);

    this.player?.on('HLS_ERROR', this.playFailEvent);
    this.player?.on('loadedmetadata', this.handleLoadMetaData);
  }

  handleDefinitionChange(e: { from: string; to: string }) {
    const target = Object.entries(VideoRate).find((item) => item[1] === e.to);
    if (target) {
      this.definition = target[0] as VideoRateKey;
      localStorage.setItem('definition', target[0]);
    }
  }

  handleLoadMetaData() {
    if (this.retryCount > RECONNECT_LIMIT) {
      this.player?.pause();
      return;
    }

    this.player?.play();
  }

  async playFailEvent(data: {
    errorType: ErrorData['type'],
    errorDetails: ErrorData['details'],
    errorFatal: ErrorData['fatal'],
  }) {
    const SentryVideoContext = {
      ...data,
      ...this.SentryVideoBaseContext
    };
    this.setSentryContext(SentryVideoContext);


    if (data.errorType === 'networkError' && data.errorDetails === 'fragLoadError') {
      const result = await fetch(this.videoRawSrc, {
        method: 'head'
      })

      if (result.status === 403) {
        this.isRetry = true
        this.retryCount += 1
        Sentry.captureMessage('【HLS_ERROR】视频播放过程错误，片段因auth_key超时加载错误')
        return
      }
      
      Sentry.captureMessage('【HLS_ERROR】视频播放过程错误，网络错误，片段加载错误')
      return;
    }

    if (!data.errorFatal) {
      Sentry.captureMessage('【HLS_ERROR】视频播放过程错误但fatal→false');
      return;
    }

    this.last_play_time = this.player?.currentTime;

    if (this.retryCount > RECONNECT_LIMIT) {
      this.$Message.error({ content: `视频加载错误，请刷新页面重新播放`, time: 4000 });
      (this.player as any).hls?.stopLoad();
      this.player?.pause();
      this.error = `视频加载错误，请刷新页面`;

      Sentry.captureMessage('【HLS_ERROR】视频播放过程错误且重试失败');
      return;
    }

    this.$Message.warn({ content: `加载视频错误，自动重试中……`, time: 4000 });
    this.isRetry = true;
    this.retryCount += 1;
  }

  @Watch('isRetry')
  handleRetryChanged(newVal: boolean, oldVal: boolean) {
    if (newVal) {
      // const last_play_time = this.player?.currentTime || 0;
      this.initVideo();
    }
  }

  get holderOptions() {
    return this.isPublic && (this.environment as any).sharedKey
      ? {
          holderId: (this.environment as any).sharedKey,
          holderType: 'PASTE'
        }
      : (this.environment as any)?.sharedKey
          ? {
              holderId: (this.environment as any).sharedKey,
              holderType: 'COURSE'
            }
          : { holderId: null, holderType: null };
  }

  async onGetVideoPlayList(vodVideoPlayInfoId: string) {
    try {
      // const isValidateToken = await this.$PostConnection!.command('check_auth');
      // console.log("🚀 ~ file: Video.vue:227 ~ Video ~ onGetVideoPlayList ~ PostConnection:", this.$PostConnection)
      // console.log("🚀 ~ file: Video.vue:226 ~ Video ~ onGetVideoPlayList ~ isValidateToken:", isValidateToken)

      const result = await this.$getClemanClient().apollo.query<Result>({
        query: getVideoPlayListGQL,
        variables: {
          vodVideoPlayInfoId,
          ...this.holderOptions
        }
      });

      return new Promise<ResultWithObjPlayList>((resolve) => {
        const videoRes = result.data;

        if (videoRes.vodVideoPlayInfo.status.toLocaleLowerCase() !== 'normal') {
          this.error = `播放视频错误，视频状态：${
            videoRes.vodOptions.videoStatuses.find(
              (item) => item.value === videoRes.vodVideoPlayInfo.status
            )?.label || ` 未知错误${videoRes.vodVideoPlayInfo.status}`
          }`;
        }

        const _playList: Record<VideoRateKey, Omit<PlayItem, 'definition'> | undefined> = {
          FD: undefined,
          SD: undefined,
          HD: undefined,
          OD: undefined,
          '2K': undefined,
          '4K': undefined,
          AUTO: undefined
        };

        Array.isArray(videoRes?.vodVideoPlayInfo.playList) &&
          !!videoRes?.vodVideoPlayInfo.playList.length &&
          Object.values(videoRes?.vodVideoPlayInfo.playList).forEach((item: PlayItem) => {
            const { definition, ...other } = item;
            if (item.definition in _playList) {
              _playList[item.definition as VideoRateKey] = other;
            }
          });

        resolve({
          ...videoRes,
          vodVideoPlayInfo: {
            ...videoRes?.vodVideoPlayInfo,
            playList: _playList
          }
        });
      });
    } catch (e) {
      this.error = `获取视频源错误，${(e as any).message}`;
      console.log('catch error', (e as any).message);
      this.error = (e as any).message;
      this.$Message.error({ content: (e as any).message, time: 3000 });
      if (e instanceof ApolloError) {
        Sentry.setContext('ApolloError', {
          message: e.graphQLErrors[0]?.message ?? '未知错误信息'
        });
      }

      Sentry.setContext('Video', {
        vodVideoPlayInfoId,
        ...this.holderOptions
      });
      Sentry.captureMessage('Stream视频获取源地址错误', {
        level: 'fatal'
      });
    }
  }

  initNormalVideo() {
    this.player = new Player({
      el: this.$refs.video as HTMLDivElement,
      url: this.content.url,
      poster: this.content.poster,
      playbackRate: [0.5, 1, 1.25, 1.5, 2],
      defaultPlaybackRate: this.content.defaultSpeed,
      pip: true,
      autoplay:
        window.storyteller.topContet?.$id === this.content.$id &&
        ['是', true].includes(this.content?.autoFullscreen || ''),
      fluid: true,
      // @ts-ignore
      ignores: ['skip', 'enter']
    });

    const hdvVideo = new HdvVideo(this.content.url);
    if (hdvVideo.isHdv) {
      this.player.emit('resourceReady', [
        { name: '原画', url: hdvVideo.origin },
        { name: '高清', url: hdvVideo.hdv720 },
        { name: '流畅', url: hdvVideo.hdv480 }
      ]);
    }

    this.initVideoEvent();
  }

  initStreamVideo() {
    import('xgplayer-hls.js').then(async (module) => {
      const _url = this.content.url.replace(/^vod:/, '');

      this.onGetVideoPlayList(_url)
        .then((videoRes) => {
          if (videoRes) {
            if (!this.player) {

              this.videoRawSrc = videoRes.vodVideoPlayInfo.playList.AUTO!.playUrl;

              // eslint-disable-next-line new-cap
              this.player = new module['default']({
                el: this.$refs.video as HTMLDivElement,
                url: videoRes?.vodVideoPlayInfo?.playList?.AUTO?.playUrl,
                poster: (this.content.poster || videoRes?.vodVideoPlayInfo.coverUrl) ?? '',
                playbackRate: [0.75, 1.0, 1.25, 1.5, 2.0], // 播放倍速
                defaultPlaybackRate: this.content.defaultSpeed,
                pip: true,
                autoplay:
                  window.storyteller.topContet?.$id === this.content.$id &&
                  ['是', true].includes(this.content?.autoFullscreen || ''),
                fluid: true,
                useHls: useHlsValue(),
                // @ts-ignore
                ignores: ['skip', 'enter']
              });
            } else {
              // 处理上一次视频播放的时间
              this.player.src = videoRes.vodVideoPlayInfo.playList.AUTO!.playUrl;
              this.player.reload();
              if (this.last_play_time) {
                this.player.currentTime = this.last_play_time;
                this.last_play_time = undefined;
              }
              this.isRetry = false;
              this.definition !== 'AUTO' &&
                this.player.emit('definitionChange', {
                  from: VideoRate['AUTO'],
                  to: VideoRate[this.definition]
                });
            }

            if (
              videoRes.vodVideoPlayInfo.status &&
              !videoRes.vodVideoPlayInfo.status.toLocaleLowerCase().includes('normal')
            ) {
              typeof this.player?.emit === 'function' &&
                this.player.emit('error', {
                  message: videoRes.vodVideoPlayInfo.status,
                  status: videoRes.vodOptions?.videoStatuses
                });
            }

            const ppiList = Object.keys(videoRes.vodVideoPlayInfo.playList)
              .map((key) => ({
                name: VideoRate[key as VideoRateKey],
                url: videoRes.vodVideoPlayInfo.playList[key as VideoRateKey]?.playUrl ?? ''
              }))
              .filter((item) => item.url && item);
            this.player?.emit('resourceReady', ppiList);

            if (!this.isInit) {
              this.initVideoEvent();
              this.isInit = true;
            }
          }
        })
        .catch((e: any) => {
          console.log('catch error', e.message);
          this.error = e.message;
          this.$Message.error({ content: e.message, time: 3000 });
        });
    });
  }

  handleEnd() {
    this.$store.commit('video/setEndState', true);

    if (!this.player) return;
    try {
      this.player?.exitFullscreen && this.player.exitFullscreen(this.player.root);
    } catch (error) {
      // 无全屏情况 忽略
    }
  }

  nextHook() {
    // xgplayer-pip-inner-active
    const wrapperEl = this.$refs.player as HTMLDivElement;
    if (!wrapperEl) return;
    const pipEl = wrapperEl.querySelector('.xgplayer-pip-inner-active');
    if (pipEl) return;
    this.player?.pause();
  }

  getErrorType(type: string) {
    const errorTypes = {
      media: '资源请求被用户终止',
      network: '媒体请求网络错误',
      decoder: '媒体解码出错',
      format: '媒体资源格式不支持播放',
      demux: '解封装过程出错',
      remux: '重封装出错',
      hls: 'hls解析错误',
      mse: 'MSE出错'
    };

    return errorTypes[type as keyof typeof errorTypes] ?? '其他类型错误';
  }

  get courseId() {
    const courseId =
      this.environment && this.environment.kind === 'public-preview'
        ? this.environment.sharedKey
        : '';
    return courseId;
  }

  handleVideoError(e: any) {
    this.$Message.warn({
      time: 1500,
      content: '播放错误，请刷新页面或稍后重试播放'
    });

    console.log('视频播放过程错误', e);
    // 手动触发错误监听
    Sentry.setContext('videoError', {
      ...e,
      资源地址: this.content.url
    });
    Sentry.setContext('course', {
      课程名: this.courseName,
      课程ID: this.courseId
    });
    Sentry.captureMessage(`[errorCode ${e.errorCode}]：视频播放错误`, {
      level: 'error'
    });
  }

  handleLoad() {
    // 视频准备完成但页面未重新计算完成 手动延迟
    setTimeout(() => {
      this.$emit('loaded');
    }, 1000);
  }
}
</script>
<style scoped>
.player {
  @apply flex items-center justify-center w-full rounded-motif overflow-hidden;
}

.video {
  --plyr-color-main: rgba(0, 0, 0, 0.3);
  --plyr-badge-background: var(--motif-400);
  --plyr-range-fill-background: var(--motif-400);
  --plyr-control-toggle-checked-background: var(--motif-400);
  @apply flex items-center relative w-full md:max-w-600 shadow-md my-5 md:ml-12 md:mr-12 bg-black rounded-motif overflow-hidden;
}

.loading {
  @apply flex items-center justify-center w-full h-48 bg-black;
}
</style>

<style>
#forchange-video .state {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: black;
}
</style>
